package com.dignicate.web.ui.seo

import kotlinx.browser.document

data class PageMetaInfo(
    val title: String,
    val description: String,
    val canonicalPath: String,
)

private const val SITE_BASE_URL = "https://dignicate.com"
private const val DEFAULT_TITLE = "Dignicate, LLC"
private const val DEFAULT_DESCRIPTION =
    "Dignicate, LLC is a small software studio focused on mobile app development. We design and build Android & iOS apps with Kotlin Multiplatform, Flutter, and native technologies — from architecture to long-term operation."

val routeMetaMap: Map<String, PageMetaInfo> = mapOf(
    "/" to PageMetaInfo(
        title = DEFAULT_TITLE,
        description = DEFAULT_DESCRIPTION,
        canonicalPath = "/",
    ),
    "/company" to PageMetaInfo(
        title = "Company — $DEFAULT_TITLE",
        description = "Dignicate, LLC is a small software studio founded in Tokyo in 2019. We build Android and iOS apps for clients, from new product launches to ongoing improvements.",
        canonicalPath = "/company",
    ),
    "/privacy" to PageMetaInfo(
        title = "Privacy Policy — $DEFAULT_TITLE",
        description = "Privacy Policy of Dignicate, LLC.",
        canonicalPath = "/privacy",
    ),
    "/terms" to PageMetaInfo(
        title = "Terms of Service — $DEFAULT_TITLE",
        description = "Terms of Service of Dignicate, LLC.",
        canonicalPath = "/terms",
    ),
)

fun applyPageMeta(route: String) {
    val meta = routeMetaMap[route] ?: routeMetaMap["/"]!!
    val canonicalUrl = "$SITE_BASE_URL${meta.canonicalPath}"

    // title
    document.title = meta.title

    // description
    setMetaContent("name", "description", meta.description)

    // canonical
    setLinkHref("canonical", canonicalUrl)

    // Open Graph
    setMetaContent("property", "og:url", canonicalUrl)
    setMetaContent("property", "og:title", meta.title)
    setMetaContent("property", "og:description", meta.description)

    // Twitter
    setMetaContent("name", "twitter:url", canonicalUrl)
    setMetaContent("name", "twitter:title", meta.title)
    setMetaContent("name", "twitter:description", meta.description)
}

private fun setMetaContent(attrName: String, attrValue: String, content: String) {
    val existing = document.querySelector("meta[$attrName='$attrValue']")
    if (existing != null) {
        existing.setAttribute("content", content)
    } else {
        val el = document.createElement("meta")
        el.setAttribute(attrName, attrValue)
        el.setAttribute("content", content)
        document.head?.appendChild(el)
    }
}

private fun setLinkHref(rel: String, href: String) {
    val existing = document.querySelector("link[rel='$rel']")
    if (existing != null) {
        existing.setAttribute("href", href)
    } else {
        val el = document.createElement("link")
        el.setAttribute("rel", rel)
        el.setAttribute("href", href)
        document.head?.appendChild(el)
    }
}
